import type { Generator, GeneratorType, Load } from "./types.ts";

export interface GeneratorSpec {
  id: string;
  name: string;
  type: GeneratorType;
  pMin: number;
  pMax: number;
  cost: number;
  rampRate: number;
  startupTime: number;
  initialStatus?: "off" | "on";
  initialOutput?: number;
  availability?: number;
  storageCapacity?: number; // MWh (battery only)
  initialSoC?: number;      // MWh (battery only)
  chargeEfficiency?: number;
  blackStartCapable?: boolean;
  availabilityCurve?: (gameMinutes: number) => number;
}

export interface BatterySpec extends GeneratorSpec {
  storageCapacity: number;
  initialSoC?: number;
  chargeEfficiency?: number;
}

export function makeGenerator(spec: GeneratorSpec): Generator {
  const initialStatus = spec.initialStatus ?? "off";
  const initialOutput = spec.initialOutput ?? (initialStatus === "on" ? spec.pMin : 0);
  return {
    id: spec.id,
    name: spec.name,
    type: spec.type,
    pMin: spec.pMin,
    pMax: spec.pMax,
    cost: spec.cost,
    rampRate: spec.rampRate,
    startupTime: spec.startupTime,
    output: initialOutput,
    setpoint: initialOutput,
    status: initialStatus,
    statusTimer: 0,
    availability: spec.availability ?? 1,
    storageCapacity: spec.storageCapacity,
    stateOfCharge: spec.initialSoC,
    chargeEfficiency: spec.chargeEfficiency,
    blackStartCapable: spec.blackStartCapable,
    availabilityCurve: spec.availabilityCurve,
  };
}

export function isBattery(g: Generator): boolean {
  return g.type === "battery";
}

export function isRenewable(g: Generator): boolean {
  return g.type === "wind" || g.type === "solar";
}

export function makeLoad(id: string, name: string, baseDemand: number, curve?: (m: number) => number): Load {
  return { id, name, baseDemand, curve };
}

/** Available headroom (MW) given current state. */
export function headroom(g: Generator): number {
  if (g.status !== "on") return 0;
  const ceiling = g.pMax * g.availability;
  return Math.max(0, ceiling - g.output);
}

/** Convenience: total online capacity (MW) right now. */
export function totalOnlineCapacity(generators: Generator[]): number {
  return generators
    .filter((g) => g.status === "on")
    .reduce((s, g) => s + g.pMax * g.availability, 0);
}
